import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    DocumentReference,
    MAX_DOCUMENT_REFERENCES,
} from "@common/schemas/documentReference.schema";
import {
    DraftRefsState,
    DraftRefsTarget,
    useDraftRefsByTarget,
    useTargetDraftRefs,
} from "./useDraftRefsByTarget";

export interface DocumentRefContextValue extends DraftRefsState<DocumentReference> {
    focusedDocumentId: number | null;
    setFocusedDocumentId: React.Dispatch<React.SetStateAction<number | null>>;
}

function buildDraftRefDedupKey(ref: DocumentReference): string {
    return `${ref.taskId}::${ref.documentId}`;
}

const DocumentRefContext = createContext<DocumentRefContextValue | null>(null);

interface DocumentRefProviderProps {
    taskId: string;
    children: React.ReactNode;
}

export const DocumentRefProvider: React.FC<DocumentRefProviderProps> = ({
    taskId,
    children,
}) => {
    const draftRefsState = useDraftRefsByTarget({
        taskId,
        maxRefs: MAX_DOCUMENT_REFERENCES,
        getDedupKey: buildDraftRefDedupKey,
    });
    const [focusedDocumentId, setFocusedDocumentId] = useState<number | null>(
        null,
    );

    useEffect(() => {
        setFocusedDocumentId(null);
    }, [taskId]);

    const value = useMemo(
        () => ({ ...draftRefsState, focusedDocumentId, setFocusedDocumentId }),
        [draftRefsState, focusedDocumentId],
    );

    return (
        <DocumentRefContext.Provider value={value}>
            {children}
        </DocumentRefContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useDocumentRefContextMaybe(): DocumentRefContextValue | null {
    return useContext(DocumentRefContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDraftDocumentRefsForTarget(target: DraftRefsTarget) {
    return useTargetDraftRefs({
        draftRefsState: useDocumentRefContextMaybe(),
        target,
    });
}
